import React, { useRef, useEffect } from "react";
import { DropdownMenu } from "reactstrap";
import { INotification } from "../../../../interfaces/notification";
import { Link } from "react-router-dom";
import NotificationDropdownTile from "./NotificationDropdownTile";
import { Minus, X } from "react-feather";
import { notificationState } from "./notificationState";

interface IProps {
	notifications: INotification[];
	user: any;
	notificationDropdown: boolean;
	setNotificationDropdown: any;
	notificationCount: any;
	markAsRead: (id: string) => Promise<any>;
	getAllNotifications: () => Promise<any>;
}

const NotificationDropdown: React.FC<IProps> = ({
	notifications,
	user,
	markAsRead,
	setNotificationDropdown,
	notificationDropdown,
	getAllNotifications,
	notificationCount
}) => {

	const sortByDate = () => {
		// Create a shallow copy before sorting to avoid mutating the original array
		// This ensures React can properly detect changes and prevents side effects
		return [...notifications].sort(function (a, b) {
			return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
		});
	};

	const markAllAsRead = async (e?: React.MouseEvent) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		// Use forEach instead of map since we're performing side effects, not transforming data
		notifications
			.filter((n: INotification) => n.message !== "New Ingredient Added" && !n.is_read)
			.forEach((notification) => markAsRead(notification.id).then(() => getAllNotifications()));
		setNotificationDropdown(false);
	}

	const closeButtonRef = useRef<HTMLDivElement>(null);
	const setNotificationDropdownRef = useRef(setNotificationDropdown);

	// Keep ref updated
	useEffect(() => {
		setNotificationDropdownRef.current = setNotificationDropdown;
	}, [setNotificationDropdown]);

	// Monitor state changes and ensure DOM is in sync
	useEffect(() => {
		// Use requestAnimationFrame to ensure DOM has updated
		requestAnimationFrame(() => {
			const dropdown = document.querySelector('.navbar-nav .dropdown:nth-child(2)');
			if (dropdown) {
				const hasShowClass = dropdown.classList.contains('show');

				// Force remove show class if state is false but class exists
				if (!notificationDropdown && hasShowClass) {
					dropdown.classList.remove('show');

					// Also hide the dropdown menu directly
					const dropdownMenu = dropdown.querySelector('.dropdown-menu');
					if (dropdownMenu) {
						(dropdownMenu as HTMLElement).style.display = 'none';
					}
				}
			}
		});
	}, [notificationDropdown]);

	useEffect(() => {
		const closeButton = closeButtonRef.current;
		if (!closeButton) return;

		// Store timeout IDs to clean them up on unmount
		const timeoutIds: NodeJS.Timeout[] = [];

		// Add a document-level listener to catch the event even earlier
		// Use capture phase with highest priority to run before everything
		const handleDocumentMouseDown = (e: MouseEvent) => {
			const target = e.target as Element;
			// ONLY check for the actual close button - be very specific to avoid interfering with bell icon
			// Check if the click is specifically on the X button wrapper or the X icon itself
			const isCloseButton = closeButtonRef.current &&
				(closeButtonRef.current === target ||
					closeButtonRef.current.contains(target) ||
					target.closest('[data-close-notification="true"]') === closeButtonRef.current);

			if (isCloseButton) {
				// Set flag IMMEDIATELY and synchronously - this must happen before reactstrap's toggle
				// Use shared state manager instead of global properties
				const flagTime = Date.now();
				notificationState.setCloseClicked(true, flagTime);
				notificationState.setLastClickTarget(target);

				// Also set on the target element immediately
				if (closeButtonRef.current) {
					closeButtonRef.current.setAttribute('data-close-notification-clicked', 'true');
				}

				// Don't stop propagation - let it reach our button handlers
				// Only set the flag to prevent toggle
			}
		};

		// Add document listener with capture phase - runs before everything
		document.addEventListener('mousedown', handleDocumentMouseDown, true);

		const handleMouseDown = (e: MouseEvent) => {
			// Set flag IMMEDIATELY on mousedown - this happens BEFORE click
			// This ensures the flag is set before reactstrap's toggle is called
			const flagTime = Date.now();
			notificationState.setCloseClicked(true, flagTime);
			closeButton.setAttribute('data-close-notification-clicked', 'true');

			// Store the target in shared state so toggle can check it
			notificationState.setLastClickTarget(e.target as Element);

			if (e.target) {
				(e.target as Element).setAttribute('data-close-notification-clicked', 'true');
			}

			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
		};

		const handleClick = (e: MouseEvent) => {
			// Ensure flag is still set (backup)
			notificationState.setCloseClicked(true);
			closeButton.setAttribute('data-close-notification-clicked', 'true');
			if (e.target) {
				(e.target as Element).setAttribute('data-close-notification-clicked', 'true');
			}

			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();

			// IMMEDIATELY remove show class and hide dropdown (don't wait for React state update)
			const dropdown = document.querySelector('.navbar-nav .dropdown:nth-child(2)');
			if (dropdown) {
				dropdown.classList.remove('show');

				const dropdownMenu = dropdown.querySelector('.dropdown-menu');
				if (dropdownMenu) {
					(dropdownMenu as HTMLElement).style.display = 'none';
				}
			}

			// Close immediately - use ref to ensure we have the latest function
			// Use ref to ensure we have latest function (closure-safe)
			const closeFn = setNotificationDropdownRef.current;
			if (closeFn) {
				closeFn(false);
			}
			// Also call directly as backup
			setNotificationDropdown(false);

			// Set a timestamp to track when dropdown was closed
			// This prevents toggle from running for a short period after closing
			notificationState.setJustClosed(Date.now());

			// Set a suppress toggle flag - this will be cleared only when bell icon is clicked
			// This prevents reactstrap's double-toggle from reopening the dropdown
			notificationState.setSuppressToggle(true);

			// Remove flag after a longer delay to prevent toggle from running on subsequent clicks
			// This ensures the state is fully updated and reactstrap has processed the close
			// Store timeout ID for cleanup
			const timeout1 = setTimeout(() => {
				notificationState.setCloseClicked(false);
				notificationState.setLastClickTarget(null);
				closeButton.removeAttribute('data-close-notification-clicked');
				if (e.target) {
					(e.target as Element).removeAttribute('data-close-notification-clicked');
				}
			}, 500);
			timeoutIds.push(timeout1);

			// Clear the "just closed" flag after a delay
			// The suppress toggle flag will remain until bell icon is clicked
			const timeout2 = setTimeout(() => {
				notificationState.setJustClosed(null);
			}, 600);
			timeoutIds.push(timeout2);
		};

		// Add mousedown listener FIRST - this runs before click
		// Use capture phase to run before reactstrap
		closeButton.addEventListener('mousedown', handleMouseDown, { capture: true, passive: false });
		closeButton.addEventListener('click', handleClick, { capture: true, passive: false });

		return () => {
			// Clean up event listeners
			document.removeEventListener('mousedown', handleDocumentMouseDown, true);
			closeButton.removeEventListener('mousedown', handleMouseDown, true);
			closeButton.removeEventListener('click', handleClick, true);

			// Clean up all pending timeouts to prevent memory leaks
			timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));

			// Reset notification state when component unmounts to prevent stale state
			notificationState.reset();
		};
	}, [setNotificationDropdown]);

	const handleCloseDropdown = (e: React.MouseEvent) => {
		const nativeEvent = e.nativeEvent;
		nativeEvent.preventDefault();
		nativeEvent.stopPropagation();
		nativeEvent.stopImmediatePropagation();

		// Set a flag to prevent toggle from interfering (backup in case native handler didn't run)
		if (closeButtonRef.current) {
			closeButtonRef.current.setAttribute('data-close-notification-clicked', 'true');
		}

		// Close the dropdown immediately
		setNotificationDropdown(false);
	}

	return (
		<div
			className="header-notification-container"
			onClick={(e) => e.stopPropagation()}
			onMouseDown={(e) => e.stopPropagation()}
		>
			<DropdownMenu>
				<div className="dropdown_header">
					<div className="dropdown_header_nav">
						<div>
							<span className="font-20 font-medium mr-2">All Updates</span>
							{
								notificationCount > 0 &&
								<>
									<Minus style={{ transform: "rotate(90deg)" }} size={25} />
									<span
										className="font-16 ml-2 txt_underline pointer"
										onClick={markAllAsRead}
										onMouseDown={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
									>
										Mark all as read
									</span>
								</>
							}
						</div>
						<div
							ref={closeButtonRef}
							className="pointer notification-close-button"
							data-close-notification="true"
							onClick={handleCloseDropdown}
							onMouseDown={(e) => {
								// Set flag immediately in React handler too (backup)
								notificationState.setCloseClicked(true);
								if (closeButtonRef.current) {
									closeButtonRef.current.setAttribute('data-close-notification-clicked', 'true');
								}

								const nativeEvent = e.nativeEvent;
								nativeEvent.preventDefault();
								nativeEvent.stopPropagation();
								nativeEvent.stopImmediatePropagation();

								// Prevent reactstrap's mousedown handler from toggling the dropdown
								setNotificationDropdown(false);
							}}
							style={{ display: 'inline-flex', cursor: 'pointer' }}
						>
							<X size={23} />
						</div>
					</div>
				</div>
				{
					<div className="dropdown__notifications">
						<div className="dropdown_header_main">
							<div className="font-20 font-bold ">Hi, {user.first_name} !</div>
							{
								notificationCount > 0 ?
									<><div className="font-14">View new recommendations, alerts and insights here.</div>
										<div className="dropdown__notifications_main">
											<>
												{
													// notifications.map(notification => ( ** TEMPORARILY FILTER 'New Ingredient Added' notifications
													sortByDate()
														// .concat(DEMO_NOTIFICATIONS.filter(n => n.message_type === 'Recommendation'))
														.filter((n: INotification) => n.message !== "New Ingredient Added" && !n.is_read)
														.map((notification) => (
															<NotificationDropdownTile
																key={notification.id} notification={notification}
																onClick={() => setNotificationDropdown(false)}
															/>
														))
												}
											</>
										</div>
									</> :
									<>
										<div className="font-16">You don't have any new notifications or you have marked all as Read.</div>
										<span
											className="see-all"
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setNotificationDropdown(false);
											}}
											onMouseDown={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
										>
											<Link to="/app/notifications" onClick={(e) => {
												e.stopPropagation();
											}}>View All</Link>
										</span>
									</>
							}
						</div>
					</div>
				}
			</DropdownMenu>
		</div>
	);
};

export default NotificationDropdown;
